import { apiUrl } from "./config";
import { runWordExportJob } from "./wordExportJobs";

const baseUrl = `${apiUrl}/math-bank`;

const formatErrorValue = (value) => {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    return value.map(formatErrorValue).filter(Boolean).join("、");
  }
  if (typeof value === "object") {
    return Object.entries(value)
      .map(([key, nestedValue]) => {
        const formatted = formatErrorValue(nestedValue);
        return formatted ? `${key}: ${formatted}` : "";
      })
      .filter(Boolean)
      .join("；");
  }
  return String(value);
};

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (typeof data === "string") return data;
  if (data.detail) return formatErrorValue(data.detail);
  if (data.error) return formatErrorValue(data.error);

  const entries = Object.entries(data)
    .map(([key, value]) => {
      const formatted = formatErrorValue(value);
      return formatted ? `${key}: ${formatted}` : "";
    })
    .filter(Boolean);

  return entries[0] || fallback;
};

const request = async (
  path,
  { subject, authToken, apiKey, method = "GET", body, signal } = {},
) => {
  const headers = {};

  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  if (apiKey) {
    headers["X-API-KEY"] = apiKey;
  }

  const scopedPath = subject ? `${path}${path.includes("?") ? "&" : "?"}subject=${encodeURIComponent(subject)}` : path;
  const response = await fetch(`${baseUrl}${scopedPath}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    ...(signal ? { signal } : {}),
  });

  if (response.status === 204) return null;

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    const error = new Error(
      getErrorMessage(data, `Request failed with ${response.status}`),
    );
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
};

const withQuery = (path, params = {}) => {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null && item !== "") query.append(key, item);
      });
      return;
    }
    if (value !== undefined && value !== null && value !== "") {
      query.set(key, value);
    }
  });

  const queryString = query.toString();
  return queryString ? `${path}?${queryString}` : path;
};

export const listGrades = ({ subject, authToken, apiKey, params } = {}) =>
  request(withQuery("/grades/", params), { subject, authToken, apiKey });

export const createGrade = ({ subject, authToken, apiKey, data }) =>
  request("/grades/", { subject, authToken, apiKey, method: "POST", body: data });

export const updateGrade = ({ subject, authToken, apiKey, id, data }) =>
  request(`/grades/${id}/`, { subject, authToken, apiKey, method: "PATCH", body: data });

export const deleteGrade = ({ subject, authToken, apiKey, id }) =>
  request(`/grades/${id}/`, { subject, authToken, apiKey, method: "DELETE" });

export const listUnits = ({ subject, authToken, apiKey, params } = {}) =>
  request(withQuery("/units/", params), { subject, authToken, apiKey });

export const createUnit = ({ subject, authToken, apiKey, data }) =>
  request("/units/", { subject, authToken, apiKey, method: "POST", body: data });

export const updateUnit = ({ subject, authToken, apiKey, id, data }) =>
  request(`/units/${id}/`, { subject, authToken, apiKey, method: "PATCH", body: data });

export const deleteUnit = ({ subject, authToken, apiKey, id }) =>
  request(`/units/${id}/`, { subject, authToken, apiKey, method: "DELETE" });

export const listSources = ({ subject, authToken, apiKey, params } = {}) =>
  request(withQuery("/sources/", params), { subject, authToken, apiKey });

export const createSource = ({ subject, authToken, apiKey, data }) =>
  request("/sources/", { subject, authToken, apiKey, method: "POST", body: data });

export const updateSource = ({ subject, authToken, apiKey, id, data }) =>
  request(`/sources/${id}/`, { subject, authToken, apiKey, method: "PATCH", body: data });

export const deleteSource = ({ subject, authToken, apiKey, id }) =>
  request(`/sources/${id}/`, { subject, authToken, apiKey, method: "DELETE" });

export const listQuestions = ({ subject, authToken, apiKey, params } = {}) =>
  request(withQuery("/questions/", params), { subject, authToken, apiKey });

export const searchQuestions = ({ subject, authToken, apiKey, params } = {}) =>
  request(withQuery("/questions/search/", params), { subject, authToken, apiKey });

export const findDuplicateQuestions = ({ subject, authToken, params } = {}) =>
  request(withQuery("/questions/duplicate-candidates/", params), { subject, authToken });

export const reviewDuplicateQuestions = ({ subject, authToken, data }) =>
  request("/questions/duplicate-review/", { subject, authToken, method: "POST", body: data });

export const startDuplicateQuestionScan = ({ subject, authToken, data }) =>
  request("/questions/duplicate-scan-jobs/", { subject, authToken, method: "POST", body: data });

export const getDuplicateQuestionScan = ({ subject, authToken, id }) =>
  request(`/questions/duplicate-scan-jobs/${encodeURIComponent(id)}/`, { subject, authToken });

export const getDuplicateQuestionScanResults = ({ subject, authToken, id, params }) =>
  request(withQuery(`/questions/duplicate-scan-jobs/${encodeURIComponent(id)}/results/`, params), { subject, authToken });

export const getDuplicateQuestionScanGroup = ({ subject, authToken, id, groupKey, params }) =>
  request(withQuery(`/questions/duplicate-scan-jobs/${encodeURIComponent(id)}/groups/${encodeURIComponent(groupKey)}/`, params), { subject, authToken });

export const listQuestionSources = ({ subject, authToken, params } = {}) =>
  request(withQuery("/questions/sources/", params), { subject, authToken });

export const getQuestion = ({ subject, authToken, apiKey, id }) =>
  request(`/questions/${id}/`, { subject, authToken, apiKey });

export const createQuestion = ({ subject, authToken, apiKey, data }) =>
  request("/questions/", { subject, authToken, apiKey, method: "POST", body: data });

export const updateQuestion = ({ subject, authToken, apiKey, id, data }) =>
  request(`/questions/${id}/`, {
    subject,
    authToken,
    apiKey,
    method: "PATCH",
    body: data,
  });

export const deleteQuestion = ({ subject, authToken, apiKey, id }) =>
  request(`/questions/${id}/`, { subject, authToken, apiKey, method: "DELETE" });

export const deleteQuestions = ({ subject, authToken, apiKey, ids }) =>
  request("/questions/bulk-delete/", {
    subject,
    authToken,
    apiKey,
    method: "POST",
    body: { ids },
  });

export const updateQuestions = ({ subject, authToken, apiKey, ids, data }) =>
  request("/questions/bulk-update/", {
    subject,
    authToken,
    apiKey,
    method: "POST",
    body: { ids, ...data },
  });

export const listDraftSessions = ({ subject, authToken, params } = {}) =>
  request(withQuery("/draft-sessions/", params), { subject, authToken });

export const createDraftSession = ({ subject, authToken, data }) =>
  request("/draft-sessions/", { subject, authToken, method: "POST", body: data });

export const updateDraftSession = ({ subject, authToken, id, data }) =>
  request(`/draft-sessions/${id}/`, { subject, authToken, method: "PATCH", body: data });

export const deleteDraftSession = ({ subject, authToken, id }) =>
  request(`/draft-sessions/${id}/`, { subject, authToken, method: "DELETE" });

export const listHandoutRecords = ({ subject, authToken, params } = {}) =>
  request(withQuery("/handout-records/", params), { subject, authToken });

export const getHandoutRecord = async ({ subject, authToken, id, params, signal }) => {
  // Sync once when opening, then read subsequent batches from that revision.
  if (!params?.items_revision && !Number(params?.items_offset)) {
    await request(`/handout-records/${id}/sync-questions/`, { subject, authToken, signal, method: "POST", body: {} });
  }
  return request(withQuery(`/handout-records/${id}/`, params), { subject, authToken, signal });
};
export const downloadHandoutRecord = async ({ subject, authToken, id, mode, mixedVersion, signal, onProgress }) => {
  const result = await runWordExportJob({ source: "handout_record", authToken, signal, onProgress,
    payload: { record_id: id, mode, subject: subject || "math", ...(mode === "mixed" && mixedVersion ? { mixed_version: mixedVersion } : {}) },
  });
  return result.blob;
};

export const createHandoutRecord = ({ subject, authToken, data }) =>
  request("/handout-records/", { subject, authToken, method: "POST", body: data });

export const updateHandoutRecord = ({ subject, authToken, id, data }) =>
  request(`/handout-records/${id}/`, { subject, authToken, method: "PATCH", body: data });

export const copyHandoutRecord = ({ subject, authToken, id, data }) =>
  request(`/handout-records/${id}/copy/`, { subject, authToken, method: "POST", body: data });

export const exportEditedHandoutRecord = async ({ subject, authToken, id, recordPayload,
  expectedUpdatedAt, mode, mixedVersion, signal, onProgress }) => ({ success: true,
  ...await runWordExportJob({ source: "handout_record", authToken, signal, onProgress,
    payload: { record_id: id, record_payload: recordPayload, expected_updated_at: expectedUpdatedAt,
      mode, subject: subject || "math", ...(mode === "mixed" && mixedVersion ? { mixed_version: mixedVersion } : {}) },
  }),
});

export const deleteHandoutRecord = ({ subject, authToken, id }) =>
  request(`/handout-records/${id}/`, { subject, authToken, method: "DELETE" });

export const bulkDeleteHandoutRecords = ({ subject, authToken, ids }) =>
  request("/handout-records/bulk-delete/", { subject, authToken, method: "POST", body: { ids } });

export const listQuestionFamilies = ({ subject, authToken, params } = {}) =>
  request(withQuery("/question-families/", params), { subject, authToken });
export const createQuestionFamily = ({ subject, authToken, data }) =>
  request("/question-families/", { subject, authToken, method: "POST", body: data });
export const updateQuestionFamily = ({ subject, authToken, id, data }) =>
  request(`/question-families/${id}/`, { subject, authToken, method: "PATCH", body: data });
export const deleteQuestionFamily = ({ subject, authToken, id }) =>
  request(`/question-families/${id}/`, { subject, authToken, method: "DELETE" });
export const attachFamilyQuestions = ({ subject, authToken, id, data }) =>
  request(`/question-families/${id}/attach/`, { subject, authToken, method: "POST", body: data });
export const drawFamilyQuestions = ({ subject, authToken, id, data }) =>
  request(`/question-families/${id}/draw/`, { subject, authToken, method: "POST", body: data });
export const startFamilyGeneration = ({ subject, authToken, id, data }) =>
  request(`/question-families/${id}/generate/`, { subject, authToken, method: "POST", body: data });
export const createFamilyGradeRule = ({ subject, authToken, data }) =>
  request("/question-family-grade-rules/", { subject, authToken, method: "POST", body: data });
export const updateFamilyGradeRule = ({ subject, authToken, id, data }) =>
  request(`/question-family-grade-rules/${id}/`, { subject, authToken, method: "PATCH", body: data });
export const deleteFamilyGradeRule = ({ subject, authToken, id }) =>
  request(`/question-family-grade-rules/${id}/`, { subject, authToken, method: "DELETE" });
export const listFamilyGenerationBatches = ({ subject, authToken, params } = {}) =>
  request(withQuery("/question-generation-batches/", params), { subject, authToken });
export const getFamilyGenerationBatch = ({ subject, authToken, id }) =>
  request(`/question-generation-batches/${id}/`, { subject, authToken });

export const startFamilyAnalysis = ({ subject, authToken, data }) =>
  request("/question-family-analyses/", { subject, authToken, method: "POST", body: data });
export const listFamilyAnalyses = ({ subject, authToken, params } = {}) =>
  request(withQuery("/question-family-analyses/", params), { subject, authToken });
export const getFamilyAnalysis = ({ subject, authToken, id }) =>
  request(`/question-family-analyses/${id}/`, { subject, authToken });
export const applyFamilyAnalysis = ({ subject, authToken, id, data }) =>
  request(`/question-family-analyses/${id}/apply/`, { subject, authToken, method: "POST", body: data });
export const getFamilyReplacements = ({ subject, authToken, id, params }) =>
  request(withQuery(`/question-families/${id}/replacements/`, params), { subject, authToken });

export const listExamRecords = ({ subject, authToken, params } = {}) =>
  request(withQuery("/exam-records/", params), { subject, authToken });
export const getExamRecord = ({ subject, authToken, id, params, signal }) =>
  request(withQuery(`/exam-records/${id}/`, params), { subject, authToken, signal });
export const createExamRecord = ({ subject, authToken, data }) =>
  request("/exam-records/", { subject, authToken, method: "POST", body: data });
export const updateExamRecord = ({ subject, authToken, id, data }) =>
  request(`/exam-records/${id}/`, { subject, authToken, method: "PATCH", body: data });
export const deleteExamRecord = ({ subject, authToken, id }) =>
  request(`/exam-records/${id}/`, { subject, authToken, method: "DELETE" });
export const bulkDeleteExamRecords = ({ subject, authToken, ids }) =>
  request("/exam-records/bulk-delete/", { subject, authToken, method: "POST", body: { ids } });
export const downloadExamRecord = async ({ subject, authToken, id, mode, mixedVersion, signal, onProgress }) => {
  const result = await runWordExportJob({ source: "exam_record", authToken, signal, onProgress,
    payload: { record_id: id, mode, subject: subject || "math", ...(mode === "mixed" && mixedVersion ? { mixed_version: mixedVersion } : {}) },
  });
  return result.blob;
};
